import type { SalesChecklistSection } from "@expert/contentful";

import { Accordion, Group, Stack, Text } from "@mantine/core";
import { type JSX, type ReactNode, useEffect } from "react";

import { CoverageCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/CoverageCarousel";
import { EngagementCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/EngagementCarousel";
import { RapportCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/RapportCarousel";
import { ChecklistItem } from "@/sales-tools/home-product/components/FullOfferChecklist/components/ChecklistItem";
import { CompletionBadge } from "@/sales-tools/home-product/components/FullOfferChecklist/components/CompletionBadge";
import { isSectionComplete } from "@/sales-tools/home-product/components/FullOfferChecklist/utils/isSectionComplete";
import {
    type OfferChecklistKey,
    type OfferChecklistState,
    useOfferChecklistStore,
} from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useSessionId } from "@/sdk";

export function ChecklistSection({
    section,
    onSectionComplete,
    prependSlot,
    hiddenItemKeys,
}: {
    section: SalesChecklistSection;
    onSectionComplete: (title: string) => void;
    prependSlot?: ReactNode;
    hiddenItemKeys?: string[];
}): JSX.Element | null {
    const sessionId = useSessionId();
    const data = useOfferChecklistStore((state) => state.getData(sessionId));
    const visibleItems = hiddenItemKeys?.length
        ? section.checklistItems.filter((item) => !hiddenItemKeys.includes(item.key))
        : section.checklistItems;
    const isCompleted = isSectionComplete(visibleItems, data);

    useEffect(() => {
        if (!isCompleted) return;
        onSectionComplete(section.title);
    }, [isCompleted, onSectionComplete, section.title]);

    return (
        <>
            <Accordion.Item key={section.title} value={section.title}>
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            {section.title}
                        </Text>

                        <CompletionBadge
                            totalSteps={visibleItems.length}
                            completedSteps={getCompletedSteps(visibleItems, data)}
                        />
                    </Group>
                </Accordion.Control>

                <Accordion.Panel>
                    <Stack gap="xs">
                        {prependSlot}
                        {visibleItems.map((item) => {
                            const renderType = item.additionalRenderType;
                            // After all Contentful Entries are migrated to use additionalRenderType, this can be removed
                            return (
                                <Stack gap={4} key={item.phrase}>
                                    <ChecklistItem item={item} />

                                    {renderType === "RAPPORT" && (
                                        <RapportCarousel defaultExample={item.defaultExample} />
                                    )}
                                    {renderType === "COVERAGE" && (
                                        <CoverageCarousel defaultExample={item.defaultExample} />
                                    )}
                                    {renderType != null &&
                                        ["DISCOVERY_QUESTION", "ENGAGEMENT_QUESTION"].includes(renderType) && (
                                            <EngagementCarousel examples={item.examples ?? []} />
                                        )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </>
    );
}

const getCompletedSteps = (items: SalesChecklistSection["checklistItems"], data: OfferChecklistState["data"]) => {
    return items.filter((item) => data[item.key as OfferChecklistKey]).length;
};
